import { Controller } from "stimulus"
import CryptoJS from "crypto-js"

/**
 * Device Identifier Controller
 * 
 * This controller handles device identification by generating a stable hash
 * based on available device information including Apple ID when available.
 * 
 * It provides:
 * - Consistent device identification across sessions
 * - Privacy-preserving hashing of identifiers
 * - Integration with platform-specific identifiers when available
 */
export default class extends Controller {
  static targets = ["userIdField", "deviceIdField"]
  
  connect() {
    // Generate stable identifiers when controller connects
    this.generateDeviceId()
    this.generateUserId()
  }
  
  /**
   * Generate a stable device ID based on hardware information
   * This creates a consistent hash that doesn't change between sessions
   */
  generateDeviceId() {
    // Collect device information that is stable across sessions
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      pixelRatio: window.devicePixelRatio,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    }
    
    // Add fingerprinting elements that help create a more stable identifier
    try {
      // Add canvas fingerprint (subtle differences in rendering engines)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = 200
      canvas.height = 50
      
      // Mixed text and shapes create unique fingerprints
      ctx.textBaseline = "top"
      ctx.font = "14px 'Arial'"
      ctx.fillStyle = "#F60"
      ctx.fillRect(125, 1, 62, 20)
      ctx.fillStyle = "#069"
      ctx.fillText("SeeMe.Fit", 2, 15)
      
      deviceInfo.canvasFingerprint = canvas.toDataURL()
    } catch (e) {
      console.log("Canvas fingerprinting not available")
    }
    
    // Create a stable hash of the device information
    const deviceIdString = JSON.stringify(deviceInfo)
    const deviceId = CryptoJS.SHA256(deviceIdString).toString()
    
    // Store in session storage and in form field if it exists
    localStorage.setItem('seemefit_device_id', deviceId)
    
    if (this.hasUserIdFieldTarget) {
      this.deviceIdFieldTarget.value = deviceId
    }
    
    return deviceId
  }
  
  /**
   * Generate a stable user ID based on device ID and optional Apple ID
   * If Apple ID is available through Apple Sign In, it will incorporate it
   */
  generateUserId() {
    // Get the stored device ID, regenerate if needed
    let deviceId = localStorage.getItem('seemefit_device_id')
    if (!deviceId) {
      deviceId = this.generateDeviceId()
    }
    
    // Check if we have an Apple ID from Sign In with Apple
    const appleIdData = this.getAppleIdData()
    const userIdBase = appleIdData ? `${appleIdData}_${deviceId}` : deviceId
    
    // Create a stable user ID from the combined information
    const userId = CryptoJS.SHA256(userIdBase).toString()
    
    // Store in local storage for persistence
    localStorage.setItem('seemefit_user_id', userId)
    
    // Set form field value if it exists
    if (this.hasUserIdFieldTarget) {
      this.userIdFieldTarget.value = userId
    }
    
    return userId
  }
  
  /**
   * Attempt to retrieve Apple ID information if available from Sign In with Apple
   * Returns null if unavailable
   */
  getAppleIdData() {
    // Check for stored Apple ID data from Sign in with Apple integration
    const appleIdData = localStorage.getItem('seemefit_apple_id_data')
    
    if (appleIdData) {
      return appleIdData
    }
    
    // No stored Apple ID, return null
    return null
  }
  
  /**
   * Save Apple ID data when received from Apple authentication
   * This would be called by the Apple Sign In callback
   */
  saveAppleIdData(appleData) {
    if (appleData && appleData.user) {
      // Store a hashed version of the Apple ID for privacy
      const hashedId = CryptoJS.SHA256(appleData.user).toString()
      localStorage.setItem('seemefit_apple_id_data', hashedId)
      
      // Regenerate user ID with the new Apple ID data
      this.generateUserId()
    }
  }
  
  /**
   * Get current device ID (for API calls)
   */
  getCurrentDeviceId() {
    return localStorage.getItem('seemefit_device_id') || this.generateDeviceId()
  }
  
  /**
   * Get current user ID (for API calls)
   */
  getCurrentUserId() {
    return localStorage.getItem('seemefit_user_id') || this.generateUserId()
  }
}